import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CircleCheck, CircleAlert, LoaderCircle, Store as StoreIcon } from 'lucide-react';
import { Button, cn, Popover, Select } from '@zt/ui';
import { api } from '../lib/api';
import { HelpTip } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { usePermissions } from '../lib/session';
import { useStoreChoice } from '../lib/store-choice';
import type { ActiveJobs } from '../lib/types';

/**
 * Panel üst çubuğu (F5B-21, ADR-078): çalışılan mağaza (iki veya daha çok mağazada) ve uzun işlerin ortak
 * ilerleme göstergesi (sürmekte olan ya da son 10 dakikada biten iş varken). İkisi de yoksa çubuk çizilmez.
 */
export function TopBar() {
  const { can } = usePermissions();
  const { stores } = useStoreChoice();
  const jobs = useQuery({
    queryKey: ['active-jobs'],
    queryFn: () => api.get<ActiveJobs>('/v1/admin/jobs/active'),
    enabled: can('org.job.read'),
    // Süren iş varken sık, yoksa seyrek yoklanır; bir işlem yapılınca (useApiMutation) hemen yenilenir.
    refetchInterval: (q) => ((q.state.data?.active ?? 0) > 0 ? 3_000 : 30_000),
  });
  const showStores = stores.length > 1;
  const showJobs = (jobs.data?.items.length ?? 0) > 0;
  if (!showStores && !showJobs) return null;
  return (
    <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2 border-b border-line bg-surface px-4 py-2 sm:px-8 print:hidden">
      {showJobs && <JobsIndicator data={jobs.data!} />}
      {showStores && <StoreSwitcher />}
    </div>
  );
}

function StoreSwitcher() {
  const t = useT();
  const { stores, storeId, setStoreId } = useStoreChoice();
  return (
    <div className="flex items-center gap-2">
      <StoreIcon className="h-4 w-4 shrink-0 text-ink-3" aria-hidden />
      <label htmlFor="topbar-store" className="text-sm text-ink-2">
        {t('admin.topbar.store')}
      </label>
      <Select
        id="topbar-store"
        className="h-9 w-auto min-w-40 py-1"
        value={storeId}
        onChange={(e) => setStoreId(e.target.value)}
      >
        {stores.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </Select>
      <HelpTip k="topbar.store" />
    </div>
  );
}

type Job = ActiveJobs['items'][number];

const PATHS: Record<Job['kind'], string> = {
  import: '/ice-aktarma',
  product_bulk: '/toplu-islemler',
  seo_crawl: '/seo',
  web_vitals: '/sayfa-hizi',
};
const pathOf = (j: Job) =>
  j.kind === 'import' && j.subtype === 'migration' ? '/tasima-paketi' : PATHS[j.kind];
const live = (j: Job) => j.status === 'queued' || j.status === 'running';

function JobsIndicator({ data }: { data: ActiveJobs }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const running = data.active;
  const label = running
    ? t('admin.topbar.jobsRunning', { count: running })
    : t('admin.topbar.jobsDone', { count: data.items.length });
  return (
    <div className="flex items-center gap-1">
      <Popover
        open={open}
        onOpenChange={setOpen}
        label={t('admin.topbar.jobsTitle')}
        trigger={
          <Button size="sm" variant={running ? 'secondary' : 'ghost'}>
            {running ? (
              <LoaderCircle className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden />
            ) : (
              <CircleCheck className="h-4 w-4 text-moss" aria-hidden />
            )}
            {label}
          </Button>
        }
      >
        <div className="border-b border-line px-4 py-2.5">
          <p className="font-medium text-ink">{t('admin.topbar.jobsTitle')}</p>
          <p className="text-sm text-ink-3">{t('admin.topbar.jobsLead')}</p>
        </div>
        <ul className="max-h-[60vh] divide-y divide-line overflow-y-auto">
          {data.items.map((j) => (
            <JobRow key={`${j.kind}-${j.id}`} j={j} onNavigate={() => setOpen(false)} />
          ))}
        </ul>
      </Popover>
      <HelpTip k="topbar.jobs" />
    </div>
  );
}

function JobRow({ j, onNavigate }: { j: Job; onNavigate: () => void }) {
  const t = useT();
  const { formatNumber, formatRelative } = useI18n();
  const name =
    j.kind === 'import'
      ? t(`admin.topbar.imports.${j.subtype ?? 'products'}`)
      : t(`admin.topbar.kinds.${j.kind}`);
  const pct = j.total ? Math.min(100, Math.round(((j.processed ?? 0) / j.total) * 100)) : null;
  const status = t(`admin.topbar.statuses.${j.status}`);
  return (
    <li className="space-y-1.5 px-4 py-3 text-sm">
      <div className="flex items-center justify-between gap-3">
        <Link to={pathOf(j)} onClick={onNavigate} className="font-medium text-pine hover:underline">
          {name}
        </Link>
        <span
          className={cn(
            'flex items-center gap-1 whitespace-nowrap',
            j.status === 'failed' ? 'text-rose' : j.status === 'done' ? 'text-moss' : 'text-ink-2',
          )}
        >
          {j.status === 'failed' ? (
            <CircleAlert className="h-3.5 w-3.5" aria-hidden />
          ) : j.status === 'done' ? (
            <CircleCheck className="h-3.5 w-3.5" aria-hidden />
          ) : null}
          {status}
        </span>
      </div>
      {live(j) && pct !== null && (
        <div
          role="progressbar"
          aria-label={name}
          aria-valuemin={0}
          aria-valuemax={j.total!}
          aria-valuenow={j.processed ?? 0}
          aria-valuetext={t('admin.topbar.progress', {
            processed: formatNumber(j.processed ?? 0),
            total: formatNumber(j.total!),
          })}
          className="h-1.5 overflow-hidden rounded-full bg-surface-2"
        >
          <div className="h-full rounded-full bg-pine transition-[width]" style={{ width: `${pct}%` }} />
        </div>
      )}
      <p className="text-ink-3">
        {j.total !== null && live(j)
          ? t('admin.topbar.progress', {
              processed: formatNumber(j.processed ?? 0),
              total: formatNumber(j.total),
            })
          : formatRelative(j.finishedAt ?? j.createdAt)}
      </p>
    </li>
  );
}
